import { create } from 'zustand';

import {
  listChargeItems,
  createChargeItem as apiCreateChargeItem,
  updateChargeItem as apiUpdateChargeItem,
  deactivateChargeItem as apiDeactivateChargeItem,
} from '../services/api/chargeItemsApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Charge Items store (Day-1 IPD / S1) — Zustand
 *
 * Clone of surgeryStore.js keyed by `item_id` (org-global, no dept slices —
 * dept-tag DEFERRED per S1). Idempotent + single-flight fetch — bounded
 * requests (memory rule: runtime safety / no request storms). Server
 * machine-readable error codes (INVALID_GST_RATE, CHARGE_ITEM_NOT_FOUND)
 * pass through verbatim on the result for the page's field-level mapping.
 * ═══════════════════════════════════════════════════════════════════════
 */

const useChargeItemStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  items: [],
  isLoading: false,
  error: null,
  includeInactive: false,
  loaded: false,
  _inFlight: null,

  // ── Fetch (bounded: single-flight + loaded guard) ──────────────────
  fetchChargeItems: async ({ force = false } = {}) => {
    const { _inFlight, loaded } = get();
    if (_inFlight) return _inFlight;
    if (loaded && !force) {
      return { success: true, data: { items: get().items } };
    }
    set({ isLoading: true, error: null });
    const promise = (async () => {
      const result = await listChargeItems({ active_only: !get().includeInactive });
      if (result.success) {
        set({
          items: result.data.items || [],
          isLoading: false,
          loaded: true,
          _inFlight: null,
        });
      } else {
        set({ error: result.error, isLoading: false, _inFlight: null });
      }
      return result;
    })();
    set({ _inFlight: promise });
    return promise;
  },

  // ── Create → refetch ───────────────────────────────────────────────
  createChargeItem: async (body) => {
    const result = await apiCreateChargeItem(body);
    if (result.success) {
      await get().fetchChargeItems({ force: true });
    }
    return result;
  },

  // ── Update (partial) — patch in place by id ────────────────────────
  updateChargeItem: async (item_id, body) => {
    const result = await apiUpdateChargeItem(item_id, body);
    if (result.success) {
      set((s) => ({
        items: s.items.map((r) =>
          r.item_id === item_id ? { ...r, ...(result.data || body) } : r
        ),
      }));
    }
    return result;
  },

  // ── Reactivate — same PATCH route, active:true ─────────────────────
  reactivateChargeItem: async (item_id) => get().updateChargeItem(item_id, { active: true }),

  // ── Deactivate (soft-delete) ────────────────────────────────────────
  deactivateChargeItem: async (item_id) => {
    const result = await apiDeactivateChargeItem(item_id);
    if (result.success) {
      const { includeInactive } = get();
      set((s) => ({
        items: includeInactive
          ? s.items.map((r) => (r.item_id === item_id ? { ...r, active: false } : r))
          : s.items.filter((r) => r.item_id !== item_id),
      }));
    }
    return result;
  },

  // ── Backend filter: refetch on flip ────────────────────────────────
  setIncludeInactive: (value) => {
    set({ includeInactive: !!value });
    return get().fetchChargeItems({ force: true });
  },

  clearError: () => set({ error: null }),
}));

export default useChargeItemStore;
